import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { compact } from 'lodash';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import get from 'lodash/get';
import { colors, fontSize } from '../../../constants/styleConstants';
import ListText from '../../ListText';
import bitMap from '../../../public/images/icons/companyHolder.svg';
import { EMPTY_INFO_MARK } from '../../../constants/consts';
import { isCustomProductionName } from '../../../utils/common';
import ProductionMaskIcon from '../../ProductionMaskIcon';
import TagsDivider from '../../TagsDivider';
import ImageTag from '../../ImageTag';
import OriginalName from '../../OriginalName';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
  },
  textWork: {
    fontSize: fontSize.basic,
    fontWeight: 'bold',
    color: colors.grey3,
  },
  MWInfo: {
    marginRight: theme.spacing(1),
  },
  textOther: {
    fontSize: fontSize.medium,
    color: colors.grey3,
  },
  icons: {
    color: colors.grey1,
    width: '12px',
    height: '12px',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  separatedBlockRoot: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  productionName: {
    marginRight: theme.spacing(1),
    fontWeight: 'bold',
    fontStyle: 'italic',
    fontSize: fontSize.medium,
  },
  tagsContainer: {
    width: '100%',
    textAlign: 'right',
    minHeight: 24,
  },
}));

const SeparatedBlock = ({ children, icon }) => {
  const classes = useStyles();
  return (
    <div className={classes.separatedBlockRoot}>
      •{icon || <ImageTag className={classes.icons} src={bitMap} alt="company-icon" />}
      {children}
    </div>
  );
};

const MWInfoBlock = ({ musicalWork }) => {
  const classes = useStyles();
  return (
    <span className={classes.MWInfo}>
      <OriginalName variant="bold" name={musicalWork?.name} originalName={musicalWork?.original_name} />
      {musicalWork?.composer && (
        <span
          className={classes.textOther}
        >{`, ${musicalWork?.composer?.first_name} ${musicalWork?.composer?.last_name}`}</span>
      )}
    </span>
  );
};

const ProductionCommonInfo = ({ production, profile, withEmpty }) => {
  const classes = useStyles();

  const getVenuesFromDates = React.useCallback(() => {
    const uniqVenues = {};
    (production?.dates || []).forEach(date => {
      const venueId = date?.venue?.id;
      if (!uniqVenues[venueId] && venueId) {
        uniqVenues[venueId] = date;
      }
    });
    return Object.values(uniqVenues)
      .map(dateInfo => {
        const stageInfo = compact([dateInfo?.venue?.name, dateInfo?.venueStage?.name]).join(', ');
        const cityInfo = compact([dateInfo?.city?.name, dateInfo?.country?.name]).join(', ');
        return `${cityInfo} ${stageInfo ? `(${stageInfo})` : ''}`;
      })
      .join(', '); // separation between several venues
  }, [production]);

  const performanceTypes = get(production, 'performanceTypes', []);

  return (
    <div className={classes.root}>
      {Boolean(performanceTypes?.length) && (
        <div className={classes.tagsContainer}>
          <TagsDivider tags={performanceTypes} />
        </div>
      )}
      {profile && <ProductionMaskIcon className={classes.icon} addedBy={production?.addedBy} />}
      {isCustomProductionName(production) && <div className={classes.productionName}>{`${production?.name}: `}</div>}
      <MWInfoBlock musicalWork={production?.work} />
      {(Boolean(production?.works?.length) || withEmpty) &&
        (production?.works || []).map(work => (
          <span key={work?.id}>
            <span className={classes.icon}>•</span>
            <MWInfoBlock musicalWork={work} />
          </span>
        ))}
      {!!production?.festivals?.length && (
        <SeparatedBlock>
          <ListText
            className={classes.textOther}
            list={production?.festivals || []}
            fieldToDisplay="name"
            listSeparator=", "
          />
        </SeparatedBlock>
      )}
      {(Boolean(production?.companies?.length) || withEmpty) && (
        <SeparatedBlock>
          <ListText
            className={classes.textOther}
            list={production?.companies || []}
            fieldToDisplay="name"
            listSeparator=", "
          />
        </SeparatedBlock>
      )}
      {(getVenuesFromDates() || withEmpty) && (
        <SeparatedBlock icon={<LocationOnIcon className={classes.icons} />}>
          <span className={classes.textOther}>{getVenuesFromDates() || EMPTY_INFO_MARK}</span>
        </SeparatedBlock>
      )}
    </div>
  );
};

ProductionCommonInfo.propTypes = {
  production: PropTypes.object.isRequired,
  profile: PropTypes.object,
  withEmpty: PropTypes.bool,
};

ProductionCommonInfo.defaultProps = {
  withEmpty: true,
};

export default memo(ProductionCommonInfo);
